import EmptyStateNotice from '@/components/EmptyStateNotice';
import Page from '@/components/page/Page';
import { FaCheck } from '@/components/react-icons/main';
import { useCallback, useMemo, Fragment } from 'react';
import { AreaChart, BarChart, CartesianGrid, LineChart, ReferenceLine, ResponsiveContainer, Text, Tooltip, XAxis, YAxis } from 'recharts';
import formatChartDate from './monitoringChart/formatDate.js';

const ChartComponents = {
  bar: BarChart,
  line: LineChart,
  area: AreaChart
};

// `grid` and `autoTicks` are opt-in (default off, so existing monitoring
// charts render exactly as before): grid draws faint horizontal rules;
// autoTicks lets recharts place ~5 labeled y ticks and spaced x dates instead
// of the min/max + first/last pairs.
const MonitoringChart = ({ data, title, emptyState, chartType = 'bar', tooltipFormatter, tooltipSort, children, height = 80, footer, axisPositionings = null, noSection = false, onDataPointClick, actions, margin, yDomain = null, showTime = true, yAxisFormatter, legend, minMaxY = 0, maxMinY = Infinity, cumulative = false, hideTitle = false, hideBorder = false, referenceLines = [], utc = false, grid = false, autoTicks = false }) => {
  const ChartComponent = ChartComponents[chartType] || BarChart;

  // Host-local single-day classification — pre-existing behavior, unchanged (see
  // MonitoringChart.test.jsx's characterization suite).
  const isSingleDayLocal = useMemo(() => {
    if (data.length <= 1) return true;

    const firstDate = new Date(data[0]?.time);
    const lastDate = new Date(data[data.length - 1]?.time);

    return firstDate.toDateString() === lastDate.toDateString();
  }, [data]);

  // UTC single-day classification — used only when `utc` is opted into (cloudy-ai-usage-daily-users
  // initiative's UTC seam). ISO strings always compare in UTC via `toISOString`, regardless of host TZ.
  // Short-circuits entirely unless `utc === true` (included in the deps below) — legacy (non-UTC)
  // callers must never pay for or risk this computation: `toISOString()` throws RangeError on an
  // Invalid Date, which a locale/unparseable `time` value would trigger even though such callers never
  // opted into the UTC path. If `utc` IS true but the dates themselves are invalid, fall back
  // deterministically to the same classification the local path uses (`toDateString` comparison,
  // which never throws) rather than propagating the RangeError.
  const isSingleDayUtc = useMemo(() => {
    if (!utc) return true;
    if (data.length <= 1) return true;

    const firstDate = new Date(data[0]?.time);
    const lastDate = new Date(data[data.length - 1]?.time);

    if (Number.isNaN(firstDate.getTime()) || Number.isNaN(lastDate.getTime())) {
      return firstDate.toDateString() === lastDate.toDateString();
    }

    return firstDate.toISOString().slice(0, 10) === lastDate.toISOString().slice(0, 10);
  }, [data, utc]);

  const { min, max } = useMemo(() => {
    if (yDomain) {
      return { min: yDomain[0], max: yDomain[1] };
    }

    if (!data.length) return { min: 0, max: 0 };

    const allValues = [];
    data.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key !== 'time' && key !== 'timestamp' && typeof item[key] === 'number') {
          allValues.push(item[key]);
        }
      });
    });

    if (!allValues.length) return { min: 0, max: 0 };

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    if (chartType === 'bar') {
      let maxSum = 0;
      data.forEach((item) => {
        let sum = 0;
        Object.keys(item).forEach((key) => {
          if (key !== 'time' && key !== 'timestamp' && typeof item[key] === 'number') {
            sum += item[key];
          }
        });
        maxSum = Math.max(maxSum, sum);
      });
      return { min: Math.max(0, min), max: Math.max(max, maxSum) };
    }

    return { min: Math.min(Math.max(0, min), maxMinY), max: Math.max(max, minMaxY) };
  }, [data, chartType, yDomain]);

  // NOTE: `showTime` is intentionally NOT in this deps array (pre-existing behavior, characterized
  // in MonitoringChart.test.jsx — do not "fix" without a flagged contract change). This legacy,
  // stale-closure-prone path is used only when `utc` is not opted into, preserving that memoization
  // bug byte-for-byte for existing (non-utc) consumers.
  const legacyFormatDate = useCallback((date) => formatChartDate(date, { isSingleDay: isSingleDayLocal, showTime }), [isSingleDayLocal]);

  // UTC opt-in path (cloudy-ai-usage-daily-users initiative): a fresh, non-memoized formatter on
  // every render, so it always reflects the CURRENT showTime/isSingleDay/utc props — the deps bug
  // above does not survive under this path (see MonitoringChart.test.jsx's "utc opt-in seam" block).
  const utcFormatDate = (date) => formatChartDate(date, { isSingleDay: isSingleDayUtc, showTime, utc: true });

  const formatDate = utc ? utcFormatDate : legacyFormatDate;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    let sortedPayload = payload;

    if (tooltipSort) {
      sortedPayload = [...payload].sort(tooltipSort);
    }

    const values = sortedPayload?.map((item, index) => {
      if (tooltipFormatter) {
        const [value, name] = tooltipFormatter(item.value, item.name || item.dataKey);
        return (
          <div key={index} style={{ color: item.color, marginTop: index === 0 ? '0px' : '4px' }}>
            {value} {name}
          </div>
        );
      }
      return (
        <div key={index} style={{ color: item.color, marginTop: index === 0 ? '0px' : '4px' }}>
          {item.value} {item.name || item.dataKey}
        </div>
      );
    });

    if (chartType === 'area') {
      const total = sortedPayload.reduce((sum, item) => sum + (item.value || 0), 0);
      values.unshift(
        <div key="total" style={{ color: 'gray', marginBlock: '4px' }}>
          {total} Total
        </div>
      );
    }
    const activeReferenceLinesGroups = [];

    for (let i = 0; i < referenceLines.length; i++) {
      const referenceLineGroup = referenceLines[i];

      const activeLines = referenceLineGroup.lines.filter((line) => {
        return line.x === label;
      });

      if (!activeLines.length) continue;

      activeReferenceLinesGroups.push({
        ...referenceLineGroup,
        lines: activeLines
      });
    }

    return (
      <div className={'activity-recharts-tooltip'}>
        <div style={{ fontWeight: '500', marginBottom: '6px' }}>{formatDate(label)}</div>
        {values}
        {activeReferenceLinesGroups.length > 0 && <hr className="tooltip-separator" />}
        {activeReferenceLinesGroups.map((group) => (
          <Fragment key={group.label}>
            <div className="tooltip-reference-group" style={{ color: group.color }}>
              {group.label}
            </div>
            {group.lines.map((line) => (
              <div key={line.key} className="tooltip-reference-group-item">
                {line.label}
              </div>
            ))}
          </Fragment>
        ))}
      </div>
    );
  };

  const CustomXAxisTick = ({ x, y, payload, index }) => {
    const isBarChart = chartType === 'bar';
    const startOffset = isBarChart ? 10 : 13;

    const xAxisDx = index === 0 ? (axisPositionings?.X?.start ?? 0) : (axisPositionings?.X?.end ?? 0);

    return (
      <g transform={`translate(${x + (index === 0 ? startOffset : 5)},${y})`}>
        <Text x={4} y={0} dx={xAxisDx} dy={12} textAnchor={'middle'} fill={'#babdbf'} style={{ textTransform: 'uppercase', fontSize: 9, fontWeight: 500 }}>
          {formatDate(payload.value)}
        </Text>
      </g>
    );
  };

  const CustomYAxisTick = ({ x, y, payload }) => {
    const yAxisDy = payload.value === min ? (axisPositionings?.Y?.start ?? 0) : (axisPositionings?.Y?.end ?? 0);
    const text = yAxisFormatter ? yAxisFormatter(payload.value) : payload.value;
    return (
      <g transform={`translate(${x},${y})`}>
        <Text x={0} y={0} dx={-3} dy={yAxisDy} textAnchor={'end'} fill={'#babdbf'} style={{ fontSize: 9 }}>
          {text}
        </Text>
      </g>
    );
  };

  const processedData = useMemo(() => {
    if (chartType !== 'line' || !data.length) return data;

    if (!cumulative) {
      const normalized = data.map((item) => {
        const processedItem = { ...item };
        Object.keys(item).forEach((key) => {
          if (key !== 'time' && key !== 'timestamp' && typeof item[key] === 'number') {
            processedItem[key] = item[key] || 0;
          }
        });
        return processedItem;
      });
      return normalized;
    }

    // Determine keys that should be used: any key (except time/timestamp)
    // that has at least one numeric value in the dataset
    const allKeys = new Set();
    data.forEach((item) => Object.keys(item || {}).forEach((k) => allKeys.add(k)));
    const candidateKeys = [...allKeys].filter((k) => k !== 'time' && k !== 'timestamp');
    const numericKeys = candidateKeys.filter((k) => data.some((item) => typeof item[k] === 'number'));

    const withIndex = data.map((item) => ({ item: { ...item }, time: new Date(item.time).getTime() }));
    withIndex.sort((a, b) => a.time - b.time);

    // Determine first/last non-null index for each key
    const firstIndexByKey = {};
    const lastIndexByKey = {};
    numericKeys.forEach((k) => {
      firstIndexByKey[k] = -1;
      lastIndexByKey[k] = -1;
    });

    withIndex.forEach((entry, sortedIdx) => {
      numericKeys.forEach((k) => {
        const origVal = entry.item?.[k];
        if (typeof origVal === 'number') {
          if (firstIndexByKey[k] === -1) firstIndexByKey[k] = sortedIdx;
          lastIndexByKey[k] = sortedIdx;
        }
      });
    });

    const accum = {};
    numericKeys.forEach((k) => (accum[k] = 0));

    const cumulativeByIndex = new Array(withIndex.length);
    withIndex.forEach((entry, sortedIdx) => {
      const { item } = entry;
      const newItem = { ...item };

      numericKeys.forEach((k) => {
        const first = firstIndexByKey[k];
        const last = lastIndexByKey[k];

        if (first === -1) {
          // key never appears as numeric anywhere; keep as null to avoid plotting
          newItem[k] = null;
          return;
        }

        if (sortedIdx < first || sortedIdx > last) {
          // before series starts or after it ends -> null
          newItem[k] = null;
          return;
        }

        // within active range: accumulate treating non-number as 0
        const origVal = entry.item?.[k];
        accum[k] += typeof origVal === 'number' ? origVal : 0;
        newItem[k] = accum[k];
      });

      cumulativeByIndex[sortedIdx] = newItem;
    });

    return cumulativeByIndex;
  }, [data, cumulative]);

  const uniqueReferenceLines = useMemo(() => {
    return referenceLines.map((group) => {
      const uniqueKeys = new Map();
      group.lines.forEach((line) => {
        uniqueKeys.set(line.x, line);
      });
      return { ...group, lines: Array.from(uniqueKeys.values()) };
    });
  }, [referenceLines]);

  const Section = noSection ? 'div' : Page.Section;
  const defaultMargin = chartType === 'line' ? { left: 0, right: 0, top: 10, bottom: 5 } : { left: 0, right: 0, bottom: 5 };
  const xAxisTicks = processedData.length > 1 ? [processedData[0]?.time, processedData[processedData.length - 1]?.time] : [processedData[0]?.time];

  return (
    <Section variant="chromeless">
      {processedData.length === 0 && <EmptyStateNotice icon={emptyState.icon || <FaCheck />} title={emptyState.title} description={emptyState.description} size="sm" card={true} />}

      {processedData.length > 0 && (
        <div className={`chart-container ${hideBorder ? 'chart-container-no-border' : ''}`}>
          {!hideTitle && (
            <div className="chart-header">
              <div className="chart-header-left">
                <Page.Subtitle>{title}</Page.Subtitle>
                {legend && legend.length > 1 && (
                  <div className="chart-legend">
                    {legend.map((item, index) => (
                      <div key={`${item.dataKey || 'legend'}-${index}`} className="chart-legend-item">
                        <span className="chart-legend-color" style={{ backgroundColor: item.color }} />
                        <span className="chart-legend-label">{item.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {actions && <div className="chart-actions">{actions}</div>}
            </div>
          )}
          <ResponsiveContainer width="100%" height={height}>
            <ChartComponent data={processedData} isAnimationActive={false} margin={margin || defaultMargin} onClick={onDataPointClick}>
              {grid && <CartesianGrid vertical={false} stroke="rgba(0, 0, 0, 0.06)" />}
              <XAxis tick={<CustomXAxisTick />} tickSize={0} axisLine={false} dataKey="time" interval={'preserveStartEnd'} tickFormatter={formatDate} height={14} ticks={autoTicks ? undefined : xAxisTicks} />
              <YAxis tick={<CustomYAxisTick />} tickSize={0} axisLine={false} width={autoTicks ? 35 : 25} ticks={autoTicks ? undefined : min === max ? [max] : [min, max]} tickCount={autoTicks ? 5 : undefined} domain={yDomain || (autoTicks ? [0, 'auto'] : [min, max])} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} isAnimationActive={false} position={{ y: -24 }} />

              {children}
              {uniqueReferenceLines
                .flatMap((group) => group.lines.map((line) => ({ ...line, color: group.color })))
                .map((line) => (
                  <ReferenceLine //
                    key={line.key}
                    x={line.x}
                    stroke={line.color || '#ff1744'}
                    strokeDasharray="3 3"
                    strokeOpacity={0.5}
                  />
                ))}
            </ChartComponent>
          </ResponsiveContainer>
        </div>
      )}

      {footer && <div className="chart-footer">{footer}</div>}
    </Section>
  );
};

export default MonitoringChart;
